import tw from 'twin.macro'
import React, { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { isEqual } from 'lodash'
import { CgInfo, CgCalendar, CgArrowRight } from 'react-icons/cg'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import {
  fromDayAnchorToLocalDate,
  fromExpirationAnchorToLocalDate,
  getLenientExpirationDate,
  getLenientStartDate,
} from 'modules/ProductInfo/utils/dateUtils'
import { Switch } from 'common/components/Switch/Switch'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'
import CopyToClipboardButton from 'common/components/CopyToClipboardButton/CopyToClipboardButton'
import { Input } from 'common/components/Input/Input'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { editCouponFormSchema } from './couponSchema'
import { dateInputClasses, Description } from './AddCouponForm'

export function EditCouponForm({ handleEditCoupon, coupon, isPendingRequest }) {
  const defaultValues = {
    name: coupon.name,
    alwaysActive: Boolean(coupon?.alwaysActive),
    startDate: coupon?.startDate
      ? fromDayAnchorToLocalDate(coupon.startDate)
      : fromDayAnchorToLocalDate(getLenientStartDate(new Date())),
    expiration: coupon?.expiration ? fromExpirationAnchorToLocalDate(coupon.expiration) : null,
  }

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(editCouponFormSchema),
  })
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
  } = methods

  const formState = watch()

  const onSubmit = async (data) => {
    handleEditCoupon({
      formData: {
        ...data,
        startDate: getLenientStartDate(data.startDate),
        expiration: getLenientExpirationDate(data.expiration),
      },
      coupon,
    })
  }

  // ref control
  const { addManyInputRefs, moveFocusOnKeyPress } = useFormRefsControl()
  const nameRef = useRef()
  const coupStartDateInputRef = useRef()
  const coupExpDateInputRef = useRef()
  const submitRef = useRef()

  useEffect(() => {
    addManyInputRefs([
      { ref: nameRef, name: 'name' },
      { ref: { current: coupStartDateInputRef?.current?.input }, name: 'startDate' },
      { ref: { current: coupExpDateInputRef?.current?.input }, name: 'expiration' },
      { ref: submitRef, name: 'submit' },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit(onSubmit)))

  return (
    <FormProvider {...methods}>
      <div className='divide-y divide-gray-200 overflow-auto'>
        <form onSubmit={handleSubmit(onSubmit)} id='couponForm'>
          <div className='flex flex-col px-10 py-4'>
            <div className='flex items-center self-start mb-2 bg-gray-100 rounded-md p-2 text-sm'>
              <span className='font-semibold text-tBlack'>Promo code:</span>
              <span className='ml-1'>{coupon.id || '(couponid-autogenerated)'}</span>
              <CopyToClipboardButton
                data={coupon.id || '(couponid-autogenerated)'}
                customClassName='ml-2 cursor-pointer text-gray-500'
                iconClassName='w-3.5 h-3.5'
              />
            </div>
            <div className='mb-2'>
              <label htmlFor='id' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                Coupon name
              </label>
              <Description>Shown to users during checkout, eg. "20% off"</Description>
              <Input
                name='name'
                type='text'
                placeholder='Enter coupon name'
                register={register}
                inputRef={nameRef}
                error={errors?.name?.message}
                autoFocus={true}
              />
              {errors.name && <p className='flex text-xs mt-1 text-tRed'>{errors.name.message}</p>}
            </div>
          </div>
          <div className='flex flex-col px-10 py-6 border-t border-gray-200'>
            <div className='flex flex-col mb-2'>
              <h4 className='inline-flex font-semibold text-tBlack mb-1'>Redemption Period</h4>
              <Description tw='mb-0'>When customers can redeem this coupon</Description>
            </div>
            <Switch
              name='alwaysActive'
              onChange={() => {
                clearErrors(['startDate', 'expiration'])
                setValue('alwaysActive', !formState.alwaysActive)
              }}
              label='Always valid'
              isChecked={formState.alwaysActive}
              labelClasses='!text-sm flex-1 justify-between'
              labelContainerClasses='!text-black'
            />
            <div className='flex items-end mt-3' css={[formState.alwaysActive && tw`invisible h-0 mt-0`]}>
              <div className='flex flex-col'>
                <div className='flex items-center mb-1.5'>
                  <span className='text-sm font-semibold mr-1'>Valid start</span>
                  <Tooltip
                    content={
                      <div className='w-[480px]'>
                        Dates are timezone‑friendly. The coupon starts at the beginning of the day in the earliest
                        timezone (UTC+14), ensuring all customers can use it.
                      </div>
                    }
                    triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
                  >
                    <CgInfo className='w-4 h-4' />
                  </Tooltip>
                </div>
                <DatePicker
                  name='startDate'
                  ref={coupStartDateInputRef}
                  selected={formState.startDate}
                  onChange={(date) => {
                    if (date) {
                      clearErrors('startDate')
                      setValue('startDate', date)
                    }
                  }}
                  placeholderText='mm/dd/yyyy'
                  disabled={formState.alwaysActive}
                  css={[dateInputClasses, errors.startDate && tw`ring-red-500`]}
                  showIcon={true}
                  icon={
                    <CgCalendar
                      className='!w-5 !h-5 top-1/2 -translate-y-1/2'
                      onClick={() => coupStartDateInputRef?.current?.setOpen(true)}
                    />
                  }
                  popperProps={{
                    placement: 'bottom-start',
                    strategy: 'fixed',
                  }}
                  onKeyDown={(e) => {
                    if (e.code === 'Tab') {
                      e.preventDefault()
                      e.stopPropagation()
                      coupStartDateInputRef?.current?.setOpen(false)
                      moveFocusOnKeyPress(e)
                    }
                  }}
                  onCalendarOpen={() => {
                    coupExpDateInputRef?.current?.setOpen(false)
                  }}
                />
              </div>
              <CgArrowRight className='w-6 h-6 text-tBlack mx-4 mb-2' />
              <div className='flex flex-col'>
                <div className='flex items-center mb-1.5'>
                  <span className='text-sm font-semibold mr-1'>Valid end</span>
                  <Tooltip
                    content={
                      <div className='w-[480px]'>
                        Dates are timezone‑friendly. The coupon expires at the end of the day in the latest timezone
                        (UTC‑12), ensuring all customers can use it.
                      </div>
                    }
                    triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
                  >
                    <CgInfo className='w-4 h-4' />
                  </Tooltip>
                </div>
                <DatePicker
                  name='expiration'
                  ref={coupExpDateInputRef}
                  selected={formState.expiration}
                  onChange={(date) => {
                    if (date) {
                      clearErrors('expiration')
                      setValue('expiration', date)
                    }
                  }}
                  placeholderText='mm/dd/yyyy'
                  disabled={formState.alwaysActive}
                  css={[dateInputClasses, errors.expiration && tw`ring-red-500`]}
                  showIcon={true}
                  icon={
                    <CgCalendar
                      className='!w-5 !h-5 top-1/2 -translate-y-1/2'
                      onClick={() => coupExpDateInputRef?.current?.setOpen(true)}
                    />
                  }
                  popperProps={{
                    placement: 'bottom-start',
                    strategy: 'fixed',
                  }}
                  onKeyDown={(e) => {
                    if (e.code === 'Tab') {
                      e.preventDefault()
                      e.stopPropagation()
                      coupExpDateInputRef?.current?.setOpen(false)
                      moveFocusOnKeyPress(e)
                    }
                  }}
                  onCalendarOpen={() => {
                    coupStartDateInputRef?.current?.setOpen(false)
                  }}
                />
              </div>
            </div>
            {errors.startDate && <p className='flex text-xs mt-1 text-tRed'>{errors.startDate.message}</p>}
            {errors.expiration && <p className='flex text-xs mt-1 text-tRed'>{errors.expiration.message}</p>}
          </div>
        </form>
      </div>
      <UpdateActions
        loading={isPendingRequest}
        handleSubmit={handleSubmit}
        disabled={isEqual(formState, defaultValues) || formState.name.trim() === ''}
        actionText='Save'
        form='couponForm'
        ref={submitRef}
        hideDelete={true}
      />
    </FormProvider>
  )
}
